import React, { useEffect, useState, useCallback, useRef } from "react";
import "./attendee-sdk.css";
import ZoomMtgEmbedded, { SuspensionViewType } from "@zoom/meetingsdk/embedded";
import MeetingSdkProps from "../../interfaces/meeting-sdk";
import debounce from "lodash.debounce";
import { Modifier, ModifierArguments } from "@popperjs/core";
import { toast } from "react-toastify";
import VideoPopup from "../../components/video-pop-up/video-pop-up";
import sendHeartbeat from "../../services/attendee-login/useHeartbeat";
import { useNavigate } from 'react-router-dom';
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

const AttenchatCustomStyle: Modifier<"customStyle", {}> = {
  name: "customStyle",
  enabled: true,
  phase: "write",
  fn: ({ state }: ModifierArguments<{}>) => {
    state.elements.popper.classList.add("attendee-chat-style");
  },
};

const chatModifiers = [AttenchatCustomStyle];
const client = ZoomMtgEmbedded.createClient();
const AttendeeMeetingView: React.FC<MeetingSdkProps> = ({
  meetingNumber,
  password,
  userName,
  userEmail,
  zakToken,
  tk,
  signature,
  setLoading,
  meetingSdkViewFlag,
  getHeightOfMeeting,
  getHeightOfMeetingResponsive,
  hideQna,
  isZoomSdkExpanded,
  showChat,
  getHeightOfQna,
  jobTitle,
  showGalleryView,
  maxVideos,
}) => {
  const [isPopupVisible, setPopupVisible] = useState(false);
  const windowWidthRef = useRef<number>(window.innerWidth);
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const attendeeLoginUrl = `${baseUrl}/login/attendee/${meetingNumber}`;
  const sharedUserIdRef = useRef(0);
  let widthForVideo: any;
  const defaultView = showGalleryView ? "gallery" : "active";
  const intervalRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openPanels = useCallback(
    debounce(async () => {
      const meetingSDKElement = document.getElementById(
        "meetingSDKElement"
      ) as HTMLElement;
      const childElement = meetingSDKElement.querySelector(
        ".zoom-MuiBox-root.css-12efcmn"
      ) as HTMLElement;
      if (childElement) {
        const height = childElement.clientHeight;
        getHeightOfMeeting(height);
      }
      // Open Chat panel
      if (showChat) {
        const chatPanel = document.querySelector(
          '[aria-label="Chat Small Meeting6"]'
        ) as HTMLElement;

        if (chatPanel) {
          chatPanel.click();
        } else {
          console.warn("Chat panel not found.");
        }
      }

      // Open QnA panel
      const qnAPanel = document.querySelector(
        '[aria-label="QA Meeting6"]'
      ) as HTMLElement;

      if (!hideQna) {
        if (qnAPanel) {
          qnAPanel.click();
        } else {
          console.warn("QnA panel not found.");
        }
      }
    }, 1000),
    []
  );

  useEffect(() => {
    const handleResize = () => {
      windowWidthRef.current = window.innerWidth;
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    let isMounted = true;
    if (window.innerWidth < 413) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      widthForVideo = window.innerWidth * 0.9;
    } else if (window.innerWidth < 415) {
      widthForVideo = window.innerWidth * 0.9;
    } else if (window.innerWidth < 450) {
      widthForVideo = window.innerWidth * 0.85;
    } else if (window.innerWidth < 992) {
      widthForVideo = window.innerWidth * 0.9;
    } else {
      widthForVideo = 420;
    }
    if (isZoomSdkExpanded) {
      if (window.innerWidth < 413) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        widthForVideo = window.innerWidth * 0.9;
      } else if (window.innerWidth < 415) {
        widthForVideo = window.innerWidth * 0.9;
      } else if (window.innerWidth < 450) {
        widthForVideo = window.innerWidth * 0.85;
      } else if (window.innerWidth < 992) {
        widthForVideo = window.innerWidth * 0.9;
      } else if (window.innerWidth < 1490) {
        widthForVideo = window.innerWidth - 765;
      } else if (window.innerWidth > 1491 && window.innerWidth < 1920) {
        widthForVideo = window.innerWidth - 768;
      } else if (window.innerWidth > 1919 && window.innerWidth < 2000) {
        widthForVideo = window.innerWidth - 775;
      } else if (window.innerWidth > 2001 && window.innerWidth < 3100) {
        widthForVideo = window.innerWidth - 778;
      } else {
        widthForVideo = 700;
      }
    }
    const startMeeting = async () => {
      const sdkKey = process.env.REACT_APP_SDK_KEY;
      let pixelPerfectVideoWidth = 69; // 100%

      try {
        const meetingSDKElement = document.getElementById("meetingSDKElement");
        const meetingSDKChatElement = document.getElementById(
          "attendeeattndeeChatElement"
        );
        const isBelow992 = windowWidthRef.current < 992;

        if (!meetingSDKElement) {
          console.error("Meeting SDK elements not found.");
          return;
        }

        if (
          meetingSdkViewFlag === "custom" &&
          meetingSDKElement &&
          !isBelow992
        ) {
          const pixelValue = (100 / 100) * window.innerWidth; // 100 --> viewport width
          const customWidth = pixelValue;
          const customWidthMetting = Math.round(customWidth);
          if (windowWidthRef.current < 3100 && windowWidthRef.current > 2304) {
            pixelPerfectVideoWidth = 70 + 15;
          }
          if (windowWidthRef.current < 2305 && windowWidthRef.current > 2047) {
            pixelPerfectVideoWidth = 59 + 14;
          }
          if (windowWidthRef.current < 2046 && windowWidthRef.current > 1919) {
            pixelPerfectVideoWidth = 55 + 17;
          }
          if (windowWidthRef.current < 1918 && windowWidthRef.current > 1706) {
            pixelPerfectVideoWidth = 50 + 20;
          }
          widthForVideo =
            customWidthMetting - (284 + 424) - pixelPerfectVideoWidth; // 315 right + 40 + 15 space
          meetingSDKElement.setAttribute("style", "left: 450px !important;"); // 424 + 20
        }

        const initConfig: any = {
          zoomAppRoot: meetingSDKElement,
          language: "en-US",
          customize: {
            video: {
              defaultViewType: defaultView as SuspensionViewType,
              isResizable: true,
              popper: {
                disableDraggable: true,
              },
              viewSizes: {
                default: {
                  width: widthForVideo,
                  height: 0,
                },
                ribbon: {
                  width: 300,
                  height: 250,
                },
              },
            },
            chat: {
              popper: {
                disableDraggable: false,
                anchorElement: meetingSDKChatElement,
                placement: "right",
                modifiers: chatModifiers,
              },
            },
          },
        };

        if (maxVideos && maxVideos > 0 && showGalleryView) {
          initConfig.maximumVideosInGalleryView = maxVideos;
        }

        await client.init(initConfig)
          .then(() => {
          client.on("connection-change", (payload) => {
            if (payload.state === "Closed") {
              // window.location.href = attendeeLoginUrl;
              sessionStorage.clear();
              localStorage.clear();
              // window.location.href = `/feedback/${meetingNumber}`;
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = undefined;
              }
              navigate(`/feedback/${meetingNumber}`, {
                state: { email: userEmail, jobTitle: jobTitle }
              });
            }
          });
          client.on("peer-share-state-change", (payload) => {
            if (payload) {
                const { userId, action } = payload;
              showHideComponent(action, userId);
            }
          });
        }).catch((error)=>{
          toast.error(`Unable join meeting. Please login again to join the meeting.`,
          {
            onClose: () => {
              window.location.href = attendeeLoginUrl;
            },
          });
          console.log('init catch', error)
        })

        if (isMounted) {
          await client
            .join({
              signature,
              sdkKey,
              meetingNumber,
              password,
              userName,
              userEmail,
              tk: tk || "",
              zak: zakToken || "",
            })
            .then(() => {
              document.body.classList.add(`attendee-view`);
              isZoomSdkExpanded
                ? document.body.classList.add(`sdk-large-view`)
                : document.body.classList.remove(`sdk-large-view`);
              // Add gallery view class if showGalleryView is true
              if (showGalleryView) {
                document.body.classList.add(`gallery-view-enabled`);
              } else {
                document.body.classList.remove(`gallery-view-enabled`);
              }
              openPanels();
              // Listen for meeting end event
              setDefaultGallaryView();
              const payload = { connectionId:userEmail, webinarId:meetingNumber, userId:userEmail };
              const tick = async () => {
                const res = await sendHeartbeat(payload);
                if(!res.data.isMeetingLive && res.data.message == "Meeting is not live"){
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = attendeeLoginUrl;
                }
              };
              tick();
              intervalRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);
            })
            .catch((error) => {
              if (error.reason === "participant exist") {
                sessionStorage.clear();
                toast.error(
                  "You are already in the meeting on another device or tab.",
                  {
                    onClose: () => {
                      window.location.reload();
                    },
                  }
                );
              } else if (error.reason === "meeting locked") {
                setPopupVisible(true);
                sessionStorage.clear();
              } else {
                setPopupVisible(false);
                sessionStorage.clear();
                toast.error(
                  `Joining meeting: ${error.reason || error.message}`,
                  {
                    onClose: () => {
                      window.location.href = attendeeLoginUrl;
                      localStorage.clear();
                    },
                  }
                );
              }
            });
        }
      } catch (error) {
        toast.error(`Unable join meeting. Please login again to join the meeting.`,
          {
            onClose: () => {
              window.location.href = attendeeLoginUrl;
            },
          });
        sessionStorage.clear();
        setLoading(false);
      }
    };

    startMeeting();
    return () => {
      isMounted = false;
      window.removeEventListener("resize", handleResize);
    };
  }, [
    meetingNumber,
    password,
    userName,
    userEmail,
    signature,
    tk,
    zakToken,
    setLoading,
    meetingSdkViewFlag,
  ]);

  const calculateWidth = () => {
    return window.innerWidth;
  };

  const toggleFullScreen = useCallback(() => {
    const elem = document.getElementById("meetingSDKElement");
    if (elem) {
      if (!document.fullscreenElement) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
          updateSdkMeetingSize(calculateWidth());
          addBackButtonToHeader();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          updateSdkMeetingSize(420);
        }
      }
    } else {
      console.error("Element not found for full-screen mode.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to add a back button to the Zoom SDK header
  function addBackButtonToHeader() {
    // Check if the button already exists
    const backBtn = document.getElementById("backButton");
    if (backBtn) {
      backBtn.style.display = "block";
      return;
    }

    // Locate the Zoom SDK header container
    const headerContainer = document.querySelector(".css-1mctt5q"); // Adjust the class to match the actual header element you find
    if (!headerContainer) {
      console.error("Zoom SDK header not found!");
      return;
    }

    // Create the back button
    const backButton = document.createElement("button");
    backButton.id = "backButton";
    backButton.innerText = "Back";
    backButton.style.position = "absolute";
    backButton.style.right = "10px";
    backButton.style.padding = "10px";
    backButton.style.backgroundColor = "rgb(36 36 36)";
    backButton.style.border = "none";
    backButton.style.cursor = "pointer";
    backButton.style.zIndex = "1000"; // Ensure it's above other elements
    backButton.style.display = "block";
    backButton.style.color = "white";

    // Append the button to the header
    headerContainer.appendChild(backButton);

    // Add event listener for the button
    backButton.addEventListener("click", () => {
      // You can use window.history.back() or navigate to a specific URL
      document.exitFullscreen();
      updateSdkMeetingSize(widthForVideo);
      backButton.style.display = "none";
    });
  }

  // Call the function to add the back button once the Zoom SDK is initialized

  const handleFullScreenChange = useCallback(() => {
    if (!document.fullscreenElement) {
      if (meetingSdkViewFlag === "custom") {
        updateSdkMeetingSize(widthForVideo);
      } else {
        updateSdkMeetingSize(widthForVideo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, [handleFullScreenChange]);

  const updateSdkMeetingSize = (widthRef: number) => {
    // const client = ZoomMtgEmbedded.createClient();
    client.updateVideoOptions({
      defaultViewType: defaultView as SuspensionViewType,
      viewSizes: {
        default: {
          width: widthRef,
          height: 0,
        },
        ribbon: {
          width: widthRef,
          height: 0,
        },
      },
    });
  };

  const setDefaultGallaryView = () => {
    setTimeout(() => {
      const parentElement = document.querySelector(
        '.attendee-view div[aria-label="view mode tabs"]'
      );
      const qnaElement = document.querySelector(
        'button[aria-label="Q&A"]'
      ) as HTMLElement;

      if (qnaElement && hideQna) {
        qnaElement.style.display = "none";
      }

      const chatElement = document.querySelector(
        'button[aria-label="Chat"]'
      ) as HTMLElement;

      if (chatElement && !showChat) {
        chatElement.style.display = "none";
      }

      const elementChat = document.querySelector(
        '[aria-label="Chat"].zoom-MuiPopper-root.css-0'
      ) as HTMLElement;
      // if (elementQnA && meetingSdkViewFlag === 'custom') {
      if (meetingSdkViewFlag === "custom") {
        elementChat?.classList?.remove("css-0");
        elementChat?.classList?.add("css-0-1");
        const meetingSDKElement = document.getElementById(
          "meetingSDKElement"
        ) as HTMLElement;
        const childElement = meetingSDKElement.querySelector(
          '[aria-label="Zoom Web SDK Widget"]'
        ) as HTMLElement;
        if (childElement) {
          const height = childElement.clientHeight;
          getHeightOfMeeting(height);
        } else {
          console.log("Child element with the specified classes not found.");
        }
      }

      if (elementChat && meetingSdkViewFlag === "default") {
        elementChat?.classList?.add("css-0");
      }

      if (parentElement) {
        // Get all button elements within the parent
        const buttons = parentElement?.querySelectorAll("button");

        // Loop through each button element
        buttons?.forEach((button) => {
          // Check if the button's aria-label is not 'thumbnail-gallery'
          // Hide the button
          button.style.display = "none";
        });
      } else {
        console.error(
          "The parent element with the specified aria-label was not found."
        );
      }
      // Responsive height calc
      const isBelow992 = windowWidthRef.current < 992;
      if (isBelow992) {
        const fullscreenbtn = document.querySelector(
          ".fullscreen-button"
        ) as HTMLElement;
        fullscreenbtn.innerHTML = "Fullscreen";
        const meetingSDKElement = document.getElementById(
          "meetingSDKElement"
        ) as HTMLElement;
        const childElement = meetingSDKElement.querySelector(
          '[aria-label="Zoom Web SDK Widget"]'
        ) as HTMLElement;
        if (childElement) {
          const height = childElement.clientHeight;
          getHeightOfMeetingResponsive(height);
        }
      }

      const elementQna = document.querySelector(
        '[aria-label="Q&A"].zoom-MuiPopper-root'
      ) as HTMLElement;
      // if SDK view in center
      if (elementQna && !showChat) {
        elementQna?.classList?.add("css-0-2");
        const height1 = elementQna.clientHeight;
        getHeightOfQna(height1);
      }
      // if Live view in center
      if (elementQna && !isZoomSdkExpanded) {
        const height2 = elementQna.clientHeight;
        const qnaInnerElement = document.querySelector(
          ".attendee-view .css-0-2 .css-1r3td12, .attendee-view .css-0-2 .css-onpmsu"
        ) as HTMLElement;
        const innerheight2 = qnaInnerElement.clientHeight;
        qnaInnerElement.style.height = `${innerheight2}px`;
        getHeightOfQna(height2);
      }

      setLoading(false);
    }, 1500);
  };

  const shareScreenView = (mode: string) => {
    if (mode === "Stop") {
      if (meetingSdkViewFlag === "custom") {
        updateSdkMeetingSize(900);
      }
    }
  };

  const showHideComponent = async (action = '', userId : number) => {
    const liveView = document.getElementById("presentationViewer") as HTMLElement;
    const biospeaker = document.getElementById("bio-speaker") as HTMLElement;
    const fullscreenBtn = document.querySelector('.fullscreen-button') as HTMLElement;
    const liveViewLarge = document.querySelector(".live-short-view") as HTMLElement;
    const expanded = document.querySelector(".zoom-sdk-expanded") as HTMLElement;
    let liveBioSpeakerLarge = document.querySelector(".right-attendee-speaker-bio-wrapper") as HTMLElement;
    let relatedContentWrapper = document.querySelector(".related-content-wrapper") as HTMLElement;
    let attendeePresentationviewWrapper = document.querySelector(".attendee-presentationview-wrapper") as HTMLElement;
    const observer = new MutationObserver(() => {
      if(window.innerWidth < 768){
        relatedContentWrapper = document.querySelector(".related-content-wrapper") as HTMLElement;
        // If the element is found, stop observing and apply styles
        if (relatedContentWrapper) {
            observer.disconnect(); // Stop observing once the element is found
            applyStyles(action, liveView, biospeaker, fullscreenBtn, liveViewLarge, liveBioSpeakerLarge, expanded, userId, relatedContentWrapper);
        }
      }
        // If the element is found, stop observing and apply styles
        liveBioSpeakerLarge = document.querySelector(".right-attendee-speaker-bio-wrapper") as HTMLElement;
        if (liveBioSpeakerLarge) {
            observer.disconnect(); // Stop observing once the element is found
            applyStyles(action, liveView, biospeaker, fullscreenBtn, liveViewLarge, liveBioSpeakerLarge, expanded, userId, relatedContentWrapper);
        }
    });
    // Observe changes to the DOM to detect when the element is added
    observer.observe(document.body, { childList: true, subtree: true });

    // Apply styles after DOM change is detected
    const applyStyles = async (action:any, liveView:any, biospeaker:any, fullscreenBtn:any, liveViewLarge:any, liveBioSpeakerLarge:any, expanded:any, userId:any, relatedContentWrapper: any) => {
        if (action === 'Start') {
            sharedUserIdRef.current = userId;
            setTimeout(() => {
              setActiveView();
                document.body.classList.add('attendee-ss');
                if (isZoomSdkExpanded) {
                    liveViewLarge.style.display = 'none';
                    liveBioSpeakerLarge.style.display = 'none';
                    expanded.style.setProperty('left', '10px', 'important');
                    fullscreenBtn.style.display = 'none';
                } else {
                    liveView.style.display = 'none';
                    liveBioSpeakerLarge.style.display = 'none';
                    fullscreenBtn.style.display = 'none';
                }
                let newWidth: any  ;
                if(window.innerWidth < 768){
                  attendeePresentationviewWrapper.removeAttribute('style');
                  const sdkHeight = getMeetingHeight();
                  newWidth = `calc(90vw)`;
                  relatedContentWrapper.style.marginTop =  `${sdkHeight + 20 }px`;
                } else{
                  newWidth = `calc(100vw - 335px)`;
                };
                document.querySelector('.attendee-ss div[aria-label="Zoom Web SDK Widget"] > div')?.setAttribute('style', `width: ${newWidth};`);
                document.querySelector('.attendee-ss div[aria-label="Zoom Web SDK Widget"]')?.setAttribute('style', `width: ${newWidth} !important;`);
                window.addEventListener('resize', () => {
                  document.querySelector('.attendee-ss div[aria-label="Zoom Web SDK Widget"] > div')?.setAttribute('style', `width: ${newWidth};`);
                  document.querySelector('.attendee-ss div[aria-label="Zoom Web SDK Widget"]')?.setAttribute('style', `width: ${newWidth} !important;`);
                });
            }, 100);
        } else {
          if(sharedUserIdRef.current != userId){
            return false;
          }
          // Remove the dynamically applied styles before stopping screen share
          const zoomWidgetDiv = document.querySelector('.attendee-ss div[aria-label="Zoom Web SDK Widget"] > div');
          const zoomWidget = document.querySelector('.attendee-ss div[aria-label="Zoom Web SDK Widget"]');
          zoomWidgetDiv?.removeAttribute('style');
          zoomWidget?.removeAttribute('style');
          await stopScreenShareAndSetActive();
          document.body.classList.remove('attendee-ss');
          if(window.innerWidth < 768){
            getHeightOfMeetingResponsive(getMeetingHeight())
            if (isZoomSdkExpanded) {
              liveViewLarge.style.display = 'block';
              liveBioSpeakerLarge.style.display = 'block';
            } else {
                liveView.style.display = 'block';
                liveBioSpeakerLarge.style.display = 'block';
                fullscreenBtn.style.display = 'block';
            }
          }else{
            if (isZoomSdkExpanded) {
              liveViewLarge.style.display = 'block';
              liveBioSpeakerLarge.style.display = 'block';
              expanded.style.setProperty('left', '448px', 'important');
            } else {
                liveView.style.display = 'block';
                liveBioSpeakerLarge.style.display = 'block';
                fullscreenBtn.style.display = 'block';
            }
          }
          setTimeout(() => {
            updateSdkMeetingSize(widthForVideo);
          },200);
        }
    };
  };

  const stopScreenShareAndSetActive = () => {
    // showLoader();
    const observer = new MutationObserver(() => {
        // Select the menu list containing view items
        const menuItems = document.querySelectorAll('.zoom-MuiList-root li');
        menuItems.forEach((item) => {
            const span = item.querySelector('span');
            // Check for the appropriate view based on showGalleryView prop
            const targetViewText = showGalleryView ? 'Gallery' : 'Active';
            if (span && span.textContent === targetViewText) {
                // (span as HTMLLIElement).click();
                setTimeout(()=>{
                  if(span.parentElement?.parentElement){
                    span.parentElement?.parentElement.click();
                    if(window.innerWidth < 768){
                      getHeightOfMeetingResponsive(getMeetingHeight())
                    } else {
                      getHeightOfMeeting(getMeetingHeight());
                    }
                  }
                  console.log(span.parentElement?.parentElement)
                },600)
                observer.disconnect();
            }
        });
    });
    // Start observing the DOM for changes related to the menu
    const viewButton = document.querySelector('[aria-label="View"]') as HTMLElement;
    if (viewButton && viewButton.getAttribute('aria-expanded') === 'false') {
        console.log("View button found, triggering click...");
        viewButton.click();
        observer.observe(document.body, { childList: true, subtree: true });
    } else {
        console.log("View button not found or already expanded, aborting observation...");
    }
  };

  const setActiveView = () => {
    // showLoader();
    const observer = new MutationObserver(() => {
        // Select the menu list containing "Active" items
      const menuItems = document.querySelectorAll('#menu-list-icon-side-by-side-gallery li');
          menuItems.forEach((item) => {
            const span = item.querySelector('span');
            if (span && span.textContent === 'Side-by-side: Speaker') {
              setTimeout(()=>{
                if(span.parentElement?.parentElement){
                  span.parentElement?.parentElement.click();
                }
              },100)
          }
      });
    });
    // Start observing the DOM for changes related to the menu
    const viewButton = document.querySelector('[aria-label="View"]') as HTMLElement;
    if (viewButton && viewButton.getAttribute('aria-expanded') === 'false') {
        console.log("View button found, triggering click...");
        viewButton.click();
        observer.observe(document.body, { childList: true, subtree: true });
        closeSteps();
    } else {
        console.log("View button not found or already expanded, aborting observation...");
    }
  };

  const getMeetingHeight = () => {
    const meetingSDKElement = document.getElementById("meetingSDKElement") as HTMLElement;
    const childElement = meetingSDKElement?.querySelector('[aria-label="Zoom Web SDK Widget"]') as HTMLElement;
    if (childElement) {
      const height = childElement.clientHeight;
      return height;
    }
    return 0;
  };
  const closeSteps = () => {
    const observer = new MutationObserver(() => {
      const skipButton = document.querySelector('.introjs-skipbutton')  as HTMLElement;;
      if (skipButton) {
        skipButton.click();
        // you can now do whatever you need with skipButton...
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };
  return (
    <div className="meeting-sdk-container">
      <div
        id="meetingSDKElement"
        className={`meeting-sdk-element ${
          isZoomSdkExpanded ? "zoom-sdk-expanded" : "zoom-sdk-regular"
        }`}
      ></div>
      <button onClick={toggleFullScreen} className="fullscreen-button">
        Speaker Video Full Screen
      </button>
      <VideoPopup
        visible={isPopupVisible}
        onClose={() => setPopupVisible(false)}
        attendeeLoginUrl={attendeeLoginUrl}
        eventId={meetingNumber}
        userEmail={userEmail}
      />
    </div>
  );
};

export default AttendeeMeetingView;